import type { FC, ReactNode, SVGProps } from 'react'

import { useSvgId } from '@/hooks/use-svg-id'
import { BRAND_COLORS, BRAND_GRADIENT, MONOGRAM, WORDMARK } from '@/constants/brand'

type SvgProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox'>

interface BrandGradientProps {
    id: string
    x1: number
    x2: number
}

interface DiviWordmarkProps extends SvgProps {
    tone?: 'gradient' | 'solid'
    label?: string
}

interface DiviMonogramProps extends SvgProps {
    label?: string
}

export const BrandGradient: FC<BrandGradientProps> = ({ id, x1, x2 }): ReactNode => {
    return (
        <linearGradient
            id={id}
            gradientUnits='userSpaceOnUse'
            x1={x1}
            y1='0'
            x2={x2}
            y2='0'
        >
            {BRAND_GRADIENT.map(stop => (
                <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
        </linearGradient>
    )
}

export const DiviWordmark: FC<DiviWordmarkProps> = ({
    tone = 'gradient',
    label = "Divi",
    ...props
}): ReactNode => {
    const fillId = useSvgId('divi-wordmark')

    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox={WORDMARK.viewBox}
            role='img'
            aria-label={label}
            {...props}
        >
            {tone === 'gradient' && (
                <defs>
                    <BrandGradient id={fillId} x1={0} x2={WORDMARK.span} />
                </defs>
            )}

            <g transform={WORDMARK.transform}>
                <path
                    d={WORDMARK.path}
                    fill={tone === 'gradient' ? `url(#${fillId})` : 'currentColor'}
                    fillRule='evenodd'
                />
            </g>
        </svg>
    )
}

export const DiviMonogram: FC<DiviMonogramProps> = ({
    label = "Divi monogram",
    ...props
}): ReactNode => {
    const fillId = useSvgId('divi-mark')
    const tileId = useSvgId('divi-tile')
    const glowId = useSvgId('divi-tile-glow')

    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox={MONOGRAM.viewBox}
            role='img'
            aria-label={label}
            {...props}
        >
            <defs>
                <BrandGradient id={fillId} x1={0} x2={MONOGRAM.span} />

                <linearGradient id={tileId} x1='0' y1='0' x2='1' y2='1'>
                    <stop offset='0' stopColor='#181D2A' />
                    <stop offset='1' stopColor={BRAND_COLORS.ink} />
                </linearGradient>

                <radialGradient id={glowId} cx='0.5' cy='0.5' r='0.5'>
                    <stop offset='0' stopColor={BRAND_COLORS.red} stopOpacity='0.3' />
                    <stop offset='1' stopColor={BRAND_COLORS.red} stopOpacity='0' />
                </radialGradient>
            </defs>

            <rect width='256' height='256' rx='60' fill={`url(#${tileId})`} />
            <rect width='256' height='256' rx='60' fill={`url(#${glowId})`} />
            <rect
                x='1'
                y='1'
                width='254'
                height='254'
                rx='59'
                fill='none'
                stroke='#FFFFFF'
                strokeOpacity='0.08'
                strokeWidth='2'
            />

            <g transform={MONOGRAM.transform}>
                <path d={MONOGRAM.path} fill={`url(#${fillId})`} fillRule='evenodd' />
            </g>
        </svg>
    )
}
